import React, { useEffect, useMemo, useState } from "react";
import "../../styles/timeTableReport.css";
import api from "../../config/URL";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const normalizeSchedule = (apiData) => {
  if (!apiData || !apiData.days || typeof apiData.days !== "object") {
    return { centerId: null, centerName: "", days: [] };
  }

  const normalizedDays = Object.entries(apiData.days).map(
    ([dayName, teacherArr]) => {
      const timeMap = {};

      teacherArr.forEach((teacherItem) => {
        (teacherItem.remarks || []).forEach((remark) => {
          const studentBatch = (remark.batches || []).find(
            (b) => b.type === "student",
          );

          if (!studentBatch) return;

          const time = studentBatch.batch;

          if (!timeMap[time]) timeMap[time] = [];

          timeMap[time].push({
            teacherName: teacherItem.teacher,
            curriculumCode: remark.curriculumCodes,
            students: studentBatch.students || [],
          });
        });
      });

      return {
        day: dayName,
        timeSlots: Object.entries(timeMap).map(([time, teachers]) => ({
          time,
          teachers,
        })),
      };
    },
  );

  return {
    centerId: apiData.centerId,
    centerName: apiData.centerName,
    days: normalizedDays, // ✅ ALWAYS ARRAY
  };
};

function TimeTableReport({ selectedCenter }) {
  const [loading, setLoading] = useState(false);
  const [scheduleData, setScheduleData] = useState({
    centerId: null,
    centerName: "",
    days: [],
  });
  const navigate = useNavigate();

  const handleStudentClick = (studentId) => {
    if (!studentId) return;

    navigate(`/report/studentAttendanceReport?studentId=${studentId}`, {
      state: { backUrl: "/report/timetableReport" },
    });
  };

  // const fetchSchedule = async () => {
  //   if (!selectedCenter) return;

  //   try {
  //     setLoading(true);
  //     const res = await api.get(
  //       `/globalScheduleReport?centerId=${selectedCenter}`
  //     );

  //     const normalized = normalizeSchedule(res.data);
  //     setScheduleData(normalized);
  //   } catch (err) {
  //     toast.error("Failed to fetch schedule data");
  //     setScheduleData({ centerId: null, centerName: "", days: [] });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchSchedule = async () => {
    if (!selectedCenter) return;

    try {
      setLoading(true);

      const res = await api.get(
        `/globalScheduleReport?centerId=${selectedCenter}`,
      );

      // ✅ API already returns correct structure
      setScheduleData({
        centerId: res.data.centerId,
        centerName: res.data.centerName,
        days: Array.isArray(res.data.days) ? res.data.days : [],
      });
    } catch (err) {
      console.error("Schedule fetch error:", err);
      toast.error("Failed to fetch schedule data");
      setScheduleData({ centerId: null, centerName: "", days: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedule();
  }, [selectedCenter]);

  const teachers = useMemo(() => {
    const set = new Set();

    scheduleData.days.forEach((day) =>
      day.timeSlots.forEach((slot) =>
        slot.teachers.forEach((t) => set.add(t.teacherName)),
      ),
    );

    return Array.from(set);
  }, [scheduleData.days]);

  if (loading) {
    return (
      <div className="tt-wrapper">
        <div className="loader-container">
          <div className="loading">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  if (!scheduleData.days.length) {
    return <div className="tt-wrapper">No timetable data</div>;
  }

  return (
    <div className="tt-wrapper">
      <h4 className="tt-center-title">{scheduleData.centerName}</h4>

      <div
        className={
          teachers.length > 4
            ? "tt-table-scroll tt-scroll-active"
            : "tt-table-scroll"
        }
      >
        <table
          className={
            teachers.length > 4 ? "tt-table tt-table-wide" : "tt-table"
          }
        >
          <thead>
            <tr>
              <th className="tt-sticky tt-day-col">Day</th>
              <th className="tt-sticky tt-time-col">Class Timing</th>
              {teachers.map((t) => (
                <th className="tt-teacher-name" key={t}>
                  {t}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {scheduleData.days.map((day) => {
              const totalRows = day.timeSlots.reduce((acc, slot) => {
                const hasStudents = slot.teachers.some(
                  (t) => t.students.length > 0,
                );
                return acc + (hasStudents ? 2 : 1);
              }, 0);

              let dayPrinted = false;

              return day.timeSlots.map((slot) => {
                const hasStudents = slot.teachers.some(
                  (t) => t.students.length > 0,
                );

                return (
                  <React.Fragment key={day.day + slot.time}>
                    {/* MAIN ROW */}
                    <tr>
                      {!dayPrinted && (
                        <td
                          rowSpan={totalRows}
                          className="tt-sticky tt-day-col"
                        >
                          {day.day}
                        </td>
                      )}

                      <td
                        rowSpan={hasStudents ? 2 : 1}
                        className="tt-sticky tt-time-col"
                      >
                        {slot.time}
                      </td>

                      {teachers.map((tName) => {
                        const t = slot.teachers.find(
                          (x) => x.teacherName === tName,
                        );

                        return (
                          <td key={tName}>
                            <input
                              disabled
                              className="tt-curriculum-box"
                              value={t?.curriculumCode || ""}
                            />
                          </td>
                        );
                      })}
                    </tr>

                    {/* STUDENT ROW */}
                    {hasStudents && (
                      <tr>
                        {teachers.map((tName) => {
                          const t = slot.teachers.find(
                            (x) => x.teacherName === tName,
                          );

                          return (
                            <td
                              key={tName}
                              className={
                                t ? "tt-student-cell" : "tt-empty-cell"
                              }
                            >
                              {t &&
                                t.students.map((s) => (
                                  <div
                                    key={s.id}
                                    className="tt-code-box tt-mb-1 tt-clickable"
                                    onClick={() => handleStudentClick(s.id)}
                                    style={{
                                      cursor: s.id ? "pointer" : "default",
                                    }}
                                  >
                                    {s.name}
                                  </div>
                                ))}
                            </td>
                          );
                        })}
                      </tr>
                    )}

                    {(dayPrinted = true)}
                  </React.Fragment>
                );
              });
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TimeTableReport;
